/**
 * Numbers for a visual comparison, recovered without decoding an image.
 *
 * Playwright compares images internally and keeps the result to itself: the
 * differing-pixel count exists only as prose inside the failure message, and
 * the image dimensions only inside the PNG. Rather than decode a PNG (which
 * would mean a dependency, and Principle V says no), both are read back — the
 * count from the message Playwright already formats, the dimensions from the
 * 8 bytes of header every PNG carries.
 */

/** PNG magic number: \x89 P N G \r \n \x1a \n */
const PNG_SIGNATURE = Buffer.from([0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]);

/** Byte offsets of width and height inside the IHDR chunk of a PNG. */
const IHDR_WIDTH_OFFSET = 16;
const IHDR_HEIGHT_OFFSET = 20;

/** Enough bytes to cover the signature, the chunk header and both dimensions. */
export const PNG_HEADER_BYTES = 24;

export interface ImageDimensions {
  readonly width: number;
  readonly height: number;
}

/**
 * Read a PNG's pixel dimensions from its IHDR chunk.
 *
 * Only the first 24 bytes are consulted, so the caller can hand over a partial
 * read of a large file. Returns `null` for anything that is not a PNG — JPEG
 * baselines are legal in Playwright and simply go un-measured.
 */
export function readPngDimensions(header: Buffer): ImageDimensions | null {
  if (header.length < PNG_HEADER_BYTES) return null;
  if (!header.subarray(0, PNG_SIGNATURE.length).equals(PNG_SIGNATURE)) return null;

  const width = header.readUInt32BE(IHDR_WIDTH_OFFSET);
  const height = header.readUInt32BE(IHDR_HEIGHT_OFFSET);

  // A zero dimension is not a valid PNG, and would make a ratio divide by zero.
  if (width === 0 || height === 0) return null;

  return { width, height };
}

export interface ParsedDiffMessage {
  /** Differing pixel count, from `"N pixels (ratio …) are different."` */
  readonly diffPixels?: number;
  /** Baseline dimensions, present only when the two images differ in size. */
  readonly expectedWidth?: number;
  readonly expectedHeight?: number;
  /** Actual dimensions, present only when the two images differ in size. */
  readonly actualWidth?: number;
  readonly actualHeight?: number;
  /** The snapshot name Playwright echoed, when the assertion supplied one. */
  readonly snapshotName?: string;
}

/**
 * `123 pixels (ratio 0.02 of all image pixels) are different.`
 *
 * The ratio in that sentence is rounded up to two decimals, so it is read past
 * deliberately — the caller recomputes it from the count and the dimensions.
 */
const PIXELS_DIFFERENT_RE = /(\d+) pixels \(ratio [\d.]+ of all image pixels\) are different/;

/** `Expected an image 800px by 600px, received 800px by 640px.` */
const SIZE_MISMATCH_RE = /Expected an image (\d+)px by (\d+)px, received (\d+)px by (\d+)px/;

/**
 * Whether a failure message is a size mismatch.
 *
 * The comparator cannot draw a diff across differing dimensions, so it
 * attaches expected + actual and throws instead. Callers that would otherwise
 * infer "no baseline existed" from a missing diff need this to tell the two
 * apart — without it a real failure was reported as a freshly written
 * baseline.
 */
export function isSizeMismatchFailure(message: string | null | undefined): boolean {
  if (!message) return false;
  return SIZE_MISMATCH_RE.test(message);
}

/** `  Snapshot: home.png` — only emitted when the assertion named the snapshot. */
const SNAPSHOT_NAME_RE = /^\s*Snapshot:\s*(.+?)\s*$/m;

/**
 * Pull the comparison numbers out of a Playwright snapshot failure message.
 *
 * Every field is optional and a malformed message yields an empty object: a
 * report that is missing a pixel count is a far better outcome than a reporter
 * that throws while assembling one.
 */
export function parseDiffMessage(message: string | null | undefined): ParsedDiffMessage {
  if (!message) return {};

  const result: {
    diffPixels?: number;
    expectedWidth?: number;
    expectedHeight?: number;
    actualWidth?: number;
    actualHeight?: number;
    snapshotName?: string;
  } = {};

  const pixels = PIXELS_DIFFERENT_RE.exec(message);
  if (pixels) {
    const count = Number.parseInt(pixels[1] as string, 10);
    if (Number.isFinite(count)) result.diffPixels = count;
  }

  const sizes = SIZE_MISMATCH_RE.exec(message);
  if (sizes) {
    const [, ew, eh, aw, ah] = sizes;
    const parsed = [ew, eh, aw, ah].map((v) => Number.parseInt(v as string, 10));
    if (parsed.every((v) => Number.isFinite(v))) {
      result.expectedWidth = parsed[0];
      result.expectedHeight = parsed[1];
      result.actualWidth = parsed[2];
      result.actualHeight = parsed[3];
    }
  }

  const name = SNAPSHOT_NAME_RE.exec(message);
  if (name) result.snapshotName = name[1];

  return result;
}

/**
 * Differing pixels as a fraction of the image, at full precision.
 *
 * Returns `undefined` rather than a wrong number when either input is missing,
 * so a consumer can tell "no measurement" apart from "measured zero".
 */
export function computeDiffRatio(
  diffPixels: number | undefined,
  dimensions: ImageDimensions | null | undefined,
): number | undefined {
  if (diffPixels === undefined || !dimensions) return undefined;
  const total = dimensions.width * dimensions.height;
  if (total <= 0) return undefined;
  return diffPixels / total;
}
